import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./text-input.module.scss";
import classnames from "classnames";
import { observer } from "mobx-react-lite";
import type { BaseTextInputProps } from "@whatslly/types/UI-V2/BaseElements/TextInputType";
import { FiSearch } from "react-icons/fi";
import globalStyles from "../../globalStyles";
import isNil from "lodash/isNil";

export const TextInput: React.FC<BaseTextInputProps> = observer(
  ({
    paddingInline,
    borderRadius,
    borderColor,
    marginTop,
    fontSize,
    outline,
    backgroundColor,
    fontWeight,
    marginBottom,
    inputSize,
    defaultValue,
    className,
    outlineWidth,
    color,
    type,
    placeholder,
    onChange,
    onClear,
    removeBorder,
    icon,
    removeClear,
    disabled,
    onClickOnIcon,
    containerClassName,
    inputRef,
    ...props
  }) => {
    const [valueState, setValueState] = useState<string | ReadonlyArray<string> | number | undefined>(String(defaultValue ?? ""));
    const elementInputRef = inputRef || useRef<HTMLInputElement | null>(null);
    const tuvisOnChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setValueState(e.target.value);
        if (onChange) {
          return onChange(e);
        }
      },
      [onChange]
    );

    const clearInput = () => {
      setValueState("");
      if (onClear) {
        onClear();
      }
      elementInputRef.current?.focus();
    };

    useEffect(() => {
      setValueState(props.value);
    }, [props.value]);

    return (
      <div
        className={classnames(
          styles.inputContainer,
          containerClassName,
          globalStyles[`pi-${paddingInline ?? "small"}`],
          globalStyles[`mb-${marginBottom ?? "none"}`],
          globalStyles[`fs-${fontSize ?? "default"}`],
          globalStyles[`mt-${marginTop ?? "none"}`]
        )}
      >
        {icon && <FiSearch onClick={() => onClickOnIcon?.()} className={classnames(styles.icon, { [styles.iconClickable]: onClickOnIcon })} />}
        <input
          type={type}
          placeholder={placeholder}
          onChange={tuvisOnChange}
          value={valueState}
          ref={(ref) => {
            elementInputRef.current = ref;
          }}
          disabled={disabled}
          className={classnames(
            styles.textInput,
            globalStyles[`br-${borderRadius ?? "default"}`],
            globalStyles[`fs-${fontSize ?? "default"}`],
            globalStyles[`fw-${fontWeight ?? "default"}`],
            globalStyles[`input-${inputSize ?? "default"}`],
            globalStyles[`bg-color-${backgroundColor ?? (disabled ? "disabled-input" : "main-secondary")}`],
            globalStyles[`color-${color ?? "main-primary"}`],
            globalStyles[`bc-${borderColor ?? "neutral-secondary"}`],
            globalStyles[`outline-${outline ?? "neutral-primary"}`],
            globalStyles[`outline-width-${outlineWidth ?? "default"}`],
            globalStyles[`v2ElementContainer`],
            { [styles.withIcon]: icon, [styles.noBorder]: removeBorder },
            className
          )}
          {...props}
        />
        <span
          className={classnames(styles.clearIcon, {
            [styles.active]: !isNil(valueState) && String(valueState).length > 0 && !removeClear && !disabled,
          })}
          onClick={clearInput}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" />
          </svg>
        </span>
      </div>
    );
  }
);
